// src/components/AIStatsDisplay.tsx
import React from "react";
import { AIEngineType } from "../hooks/useAIOpponent";
import { DifficultyLevel } from "./AISettings";
import { PositionContextData } from "../engine/NativeEngineClient";

interface AIStatsDisplayProps {
  isThinking: boolean;
  thinkingTime: number;
  stats: {
    thinkingTime: number;
    nodesEvaluated: number;
    depth: number;
    lastMove?: string;
    positionContext?: PositionContextData;
  } | null;
  engineType?: AIEngineType;
  difficulty?: DifficultyLevel;
  maxThinkingTime?: number;
  threadCount?: number;
  maxDepth?: number;
}

// Engine badge component
const EngineBadge: React.FC<{ engineType: AIEngineType }> = ({ engineType }) => {
  const getBadgeStyle = (): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      display: "inline-flex",
      alignItems: "center",
      gap: "4px",
      padding: "2px 8px",
      borderRadius: "12px",
      fontSize: "11px",
      fontWeight: "bold",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    };

    switch (engineType) {
      case "native":
        return {
          ...baseStyle,
          backgroundColor: "#ffebee",
          color: "#b71c1c",
          border: "1px solid #b71c1c",
        };
      case "wasm":
        return {
          ...baseStyle,
          backgroundColor: "#e3f2fd",
          color: "#1565c0",
          border: "1px solid #1565c0",
        };
      case "typescript":
        return {
          ...baseStyle,
          backgroundColor: "#fff3e0",
          color: "#e65100",
          border: "1px solid #e65100",
        };
      default:
        return {
          ...baseStyle,
          backgroundColor: "#f5f5f5",
          color: "#757575",
          border: "1px solid #bdbdbd",
        };
    }
  };

  const getEngineIcon = (): string => {
    switch (engineType) {
      case "native":
        return "🦀";
      case "wasm":
        return "⚡";
      case "typescript":
        return "📜";
      default:
        return "❓";
    }
  };

  const getEngineLabel = (): string => {
    switch (engineType) {
      case "native":
        return "Rust Native";
      case "wasm":
        return "Rust/WASM";
      case "typescript":
        return "TypeScript";
      default:
        return "None";
    }
  };

  return (
    <span style={getBadgeStyle()} title={`AI Engine: ${getEngineLabel()}`}>
      <span>{getEngineIcon()}</span>
      <span>{getEngineLabel()}</span>
    </span>
  );
};

// Difficulty badge component - v1.30.0: 6-level difficulty system
const DifficultyBadge: React.FC<{ difficulty: DifficultyLevel }> = ({ difficulty }) => {
  const getColor = (): { bg: string; text: string; border: string } => {
    switch (difficulty) {
      // New 6-level system
      case "beginner":
        return { bg: "#e8f5e9", text: "#2e7d32", border: "#2e7d32" };
      case "casual":
        return { bg: "#e3f2fd", text: "#1565c0", border: "#1565c0" };
      case "intermediate":
        return { bg: "#fff8e1", text: "#f57f17", border: "#f57f17" };
      case "advanced":
        return { bg: "#fff3e0", text: "#e65100", border: "#e65100" };
      case "expert":
        return { bg: "#ffebee", text: "#c62828", border: "#c62828" };
      case "master":
        return { bg: "#f3e5f5", text: "#6a1b9a", border: "#6a1b9a" };
      // Legacy support
      case "easy":
        return { bg: "#e8f5e9", text: "#2e7d32", border: "#2e7d32" };
      case "medium":
        return { bg: "#fff8e1", text: "#f57f17", border: "#f57f17" };
      case "hard":
        return { bg: "#ffebee", text: "#c62828", border: "#c62828" };
      default:
        return { bg: "#f5f5f5", text: "#757575", border: "#bdbdbd" };
    }
  };

  const colors = getColor();
  const label = difficulty.charAt(0).toUpperCase() + difficulty.slice(1);

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        padding: "2px 8px",
        borderRadius: "12px",
        fontSize: "11px",
        fontWeight: "bold",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        backgroundColor: colors.bg,
        color: colors.text,
        border: `1px solid ${colors.border}`,
      }}
      title={`Difficulty: ${label}`}
    >
      <span>🎯</span>
      <span>{label}</span>
    </span>
  );
};

// Max time badge component
const MaxTimeBadge: React.FC<{ maxTime: number }> = ({ maxTime }) => {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        padding: "2px 8px",
        borderRadius: "12px",
        fontSize: "11px",
        fontWeight: "bold",
        backgroundColor: "#e0f7fa",
        color: "#00838f",
        border: "1px solid #00838f",
      }}
      title={`Max thinking time: ${maxTime} seconds`}
    >
      <span>⏱️</span>
      <span>{maxTime}s</span>
    </span>
  );
};

// Threads badge component
const ThreadsBadge: React.FC<{ threads: number }> = ({ threads }) => {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        padding: "2px 8px",
        borderRadius: "12px",
        fontSize: "11px",
        fontWeight: "bold",
        backgroundColor: threads >= 10 ? "#f3e5f5" : "#e8eaf6",
        color: threads >= 10 ? "#7b1fa2" : "#3949ab",
        border: `1px solid ${threads >= 10 ? "#7b1fa2" : "#3949ab"}`,
      }}
      title={`Parallel search threads: ${threads}`}
    >
      <span>🧵</span>
      <span>{threads} threads</span>
    </span>
  );
};

// Search Depth badge component
const LevelsBadge: React.FC<{ levels: number }> = ({ levels }) => {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        padding: "2px 8px",
        borderRadius: "12px",
        fontSize: "11px",
        fontWeight: "bold",
        backgroundColor: "#fce4ec",
        color: "#c2185b",
        border: "1px solid #c2185b",
      }}
      title={`Max search depth: ${levels} ply`}
    >
      <span>📊</span>
      <span>{levels} ply</span>
    </span>
  );
};

// Position Context display component (Position-Aware Heuristic System)
const PositionContextDisplay: React.FC<{ context: PositionContextData }> = ({ context }) => {
  // Get icon for profile type
  const getProfileIcon = (profile: string): string => {
    switch (profile) {
      case "ATTACK": return "⚔️";
      case "DEFENSE": return "🛡️";
      case "ENDGAME": return "👑";
      case "DEVELOPMENT": return "📈";
      case "BALANCED": return "⚖️";
      default: return "🎯";
    }
  };

  // Get color for profile type
  const getProfileColor = (profile: string): { bg: string; text: string; border: string } => {
    switch (profile) {
      case "ATTACK": return { bg: "#ffebee", text: "#c62828", border: "#c62828" };
      case "DEFENSE": return { bg: "#e3f2fd", text: "#1565c0", border: "#1565c0" };
      case "ENDGAME": return { bg: "#f3e5f5", text: "#6a1b9a", border: "#6a1b9a" };
      case "DEVELOPMENT": return { bg: "#e8f5e9", text: "#2e7d32", border: "#2e7d32" };
      case "BALANCED": return { bg: "#fff8e1", text: "#f57f17", border: "#f57f17" };
      default: return { bg: "#f5f5f5", text: "#757575", border: "#bdbdbd" };
    }
  };

  const profileColors = getProfileColor(context.profile);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        padding: "8px 12px",
        backgroundColor: "#fafafa",
        borderRadius: "6px",
        border: context.is_emergency ? "2px solid #c62828" : "1px solid #e0e0e0",
        fontSize: "12px",
        marginTop: "8px",
      }}
    >
      {/* Header row with phase and profile */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
        <span
          style={{
            fontWeight: "bold",
            color: "#424242",
          }}
        >
          🧠 AI Analysis:
        </span>
        <span
          style={{
            padding: "2px 6px",
            borderRadius: "4px",
            backgroundColor: "#e0e0e0",
            color: "#424242",
            fontSize: "11px",
          }}
        >
          {context.game_phase}
        </span>
        <span
          style={{
            padding: "2px 6px",
            borderRadius: "4px",
            backgroundColor: profileColors.bg,
            color: profileColors.text,
            border: `1px solid ${profileColors.border}`,
            fontSize: "11px",
            fontWeight: "bold",
          }}
          title={`Heuristic Profile: ${context.profile}`}
        >
          {getProfileIcon(context.profile)} {context.profile}
        </span>
        {context.is_emergency && (
          <span
            style={{
              padding: "2px 6px",
              borderRadius: "4px",
              backgroundColor: "#ffebee",
              color: "#c62828",
              border: "1px solid #c62828",
              fontSize: "11px",
              fontWeight: "bold",
            }}
          >
            ⚠️ EMERGENCY
          </span>
        )}
      </div>

      {/* Primary goal */}
      <div style={{ display: "flex", alignItems: "center", gap: "4px", color: "#616161" }}>
        <span style={{ fontWeight: "500" }}>Primary Goal:</span>
        <span
          style={{
            padding: "1px 4px",
            borderRadius: "3px",
            backgroundColor: "#e8f5e9",
            color: "#2e7d32",
            fontSize: "11px",
          }}
        >
          {context.primary_goal}
        </span>
      </div>

      {/* All goals */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: "4px", color: "#757575", fontSize: "11px" }}>
        <span style={{ fontWeight: "500", whiteSpace: "nowrap" }}>Goals:</span>
        <span style={{ fontStyle: "italic" }}>{context.goals}</span>
      </div>
    </div>
  );
};

export const AIStatsDisplay: React.FC<AIStatsDisplayProps> = ({
  isThinking,
  thinkingTime,
  stats,
  engineType = "none",
  difficulty,
  maxThinkingTime,
  threadCount,
  maxDepth,
}) => {
  // Helper to get icon for profile type
  const getProfileIcon = (profile: string): string => {
    switch (profile) {
      case "ATTACK": return "⚔️";
      case "DEFENSE": return "🛡️";
      case "ENDGAME": return "👑";
      case "DEVELOPMENT": return "📈";
      case "BALANCED": return "⚖️";
      default: return "🎯";
    }
  };

  // Helper to get color for profile type
  const getProfileColor = (profile: string): { bg: string; text: string; border: string } => {
    switch (profile) {
      case "ATTACK": return { bg: "#ffebee", text: "#c62828", border: "#c62828" };
      case "DEFENSE": return { bg: "#e3f2fd", text: "#1565c0", border: "#1565c0" };
      case "ENDGAME": return { bg: "#f3e5f5", text: "#6a1b9a", border: "#6a1b9a" };
      case "DEVELOPMENT": return { bg: "#e8f5e9", text: "#2e7d32", border: "#2e7d32" };
      case "BALANCED": return { bg: "#fff8e1", text: "#f57f17", border: "#f57f17" };
      default: return { bg: "#f5f5f5", text: "#757575", border: "#bdbdbd" };
    }
  };

  const positionContext = stats?.positionContext;
  const profileColors = positionContext ? getProfileColor(positionContext.profile) : null;

  return (
    <div
      className={`ai-stats-panel ${isThinking ? "thinking" : ""}`}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "6px",
        padding: "8px 12px",
        backgroundColor: isThinking ? "#fff8e1" : "#e3f2fd",
        borderRadius: "6px",
        border: isThinking ? "2px solid #ffc107" : "2px solid #333",
        marginBottom: "5px",
        transition: "background-color 0.3s ease, border-color 0.3s ease",
        minHeight: "70px", // Fixed height to prevent layout shift
      }}
    >
      {/* Row 1: Main stats and badges */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {isThinking ? (
            <>
              <span
                style={{
                  display: "inline-block",
                  animation: "pulse 1s infinite",
                }}
              >
                🤔
              </span>
              <strong>AI is thinking...</strong>
              <span
                style={{
                  color: "#f57c00",
                  fontSize: "14px",
                  fontFamily: "monospace",
                  fontWeight: "bold",
                }}
              >
                {thinkingTime.toFixed(1)}s
              </span>
            </>
          ) : stats ? (
            <>
              <span>🤖</span>
              <span>
                {stats.lastMove && (
                  <>
                    <strong>Move:</strong>{" "}
                    <span style={{
                      fontFamily: "monospace",
                      backgroundColor: "#e3f2fd",
                      padding: "1px 6px",
                      borderRadius: "4px",
                      fontWeight: "bold",
                      color: "#1565c0"
                    }}>
                      {stats.lastMove}
                    </span>{" "}
                    |{" "}
                  </>
                )}
                <strong>Time:</strong> {stats.thinkingTime.toFixed(2)}s |{" "}
                <strong>Nodes:</strong> {stats.nodesEvaluated.toLocaleString()} |{" "}
                <strong>Depth:</strong> {stats.depth}
              </span>
            </>
          ) : (
            <>
              <span>🤖</span>
              <strong>AI Ready</strong>
            </>
          )}
        </div>

        {/* Settings badges - Always visible when engine is active */}
        {engineType !== "none" && (
          <div style={{ display: "flex", alignItems: "center", gap: "6px", marginLeft: "16px" }}>
            {difficulty && <DifficultyBadge difficulty={difficulty} />}
            {maxDepth !== undefined && <LevelsBadge levels={maxDepth} />}
            {maxThinkingTime !== undefined && <MaxTimeBadge maxTime={maxThinkingTime} />}
            {threadCount !== undefined && <ThreadsBadge threads={threadCount} />}
            <EngineBadge engineType={engineType} />
          </div>
        )}
      </div>

      {/* Row 1.5: AI Settings note */}
      {engineType !== "none" && (
        <div
          style={{
            textAlign: "right",
            fontSize: "13px",
            color: "#666",
            fontStyle: "italic",
          }}
        >
          Change these AI Settings from the{" "}
          <a
            href="/ai-settings"
            style={{
              color: "#1565c0",
              textDecoration: "underline",
            }}
          >
            AI Settings
          </a>{" "}
          page
        </div>
      )}

      {/* Row 2: Position Context (inside the same panel) */}
      {positionContext && !isThinking && profileColors && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexWrap: "wrap",
            paddingTop: "4px",
            borderTop: "1px solid #e0e0e0",
            fontSize: "12px",
          }}
        >
          <span style={{ fontWeight: "bold", color: "#424242" }}>🧠 AI Analysis:</span>
          <span
            style={{
              padding: "2px 6px",
              borderRadius: "4px",
              backgroundColor: "#e0e0e0",
              color: "#424242",
              fontSize: "11px",
            }}
          >
            {positionContext.game_phase}
          </span>
          <span
            style={{
              padding: "2px 6px",
              borderRadius: "4px",
              backgroundColor: profileColors.bg,
              color: profileColors.text,
              border: `1px solid ${profileColors.border}`,
              fontSize: "11px",
              fontWeight: "bold",
            }}
            title={`Heuristic Profile: ${positionContext.profile}`}
          >
            {getProfileIcon(positionContext.profile)} {positionContext.profile}
          </span>
          {positionContext.is_emergency && (
            <span
              style={{
                padding: "2px 6px",
                borderRadius: "4px",
                backgroundColor: "#ffebee",
                color: "#c62828",
                border: "1px solid #c62828",
                fontSize: "11px",
                fontWeight: "bold",
              }}
            >
              ⚠️ EMERGENCY
            </span>
          )}
          <span style={{ color: "#616161" }}>
            <strong>Goal:</strong>{" "}
            <span
              style={{
                padding: "1px 4px",
                borderRadius: "3px",
                backgroundColor: "#e8f5e9",
                color: "#2e7d32",
                fontSize: "11px",
              }}
            >
              {positionContext.primary_goal}
            </span>
          </span>
          <span style={{ color: "#757575", fontSize: "11px", fontStyle: "italic" }}>
            ({positionContext.goals})
          </span>
        </div>
      )}

      {/* Add pulse animation for thinking state */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}
      </style>
    </div>
  );
};

export default AIStatsDisplay;
